import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cachedGet } from '../../lib/api-client';
import { Spinner } from '../../components/kit/Spinner';
import { EmptyState } from '../../components/kit/EmptyState';
import { ErrorState } from '../../components/kit/ErrorState';
import { StaleBanner } from '../../components/app/StaleBanner';
import { formatISTDate, daysUntil } from '../../lib/format';
import { haptic } from '../../lib/haptic';

type VehicleDocType = 'INSURANCE' | 'PUC' | 'FITNESS' | 'PERMIT' | 'ROAD_TAX' | 'REGISTRATION';

type VehicleResp = {
  vehicle: {
    id: string;
    regNumber: string;
    make: string;
    model: string;
    insuranceExpiry?: string | null;
    pucExpiry?: string | null;
    fitnessExpiry?: string | null;
    permitExpiry?: string | null;
    roadTaxExpiry?: string | null;
    registrationExpiry?: string | null;
    hasImage?: boolean;
    complianceDocs?: Array<{
      type: VehicleDocType;
      updatedAt: string;
      filename: string | null;
    }>;
  } | null;
};

// Lifting this helper into a shared util (alongside the same one in
// ProfileRoute.tsx) is an out-of-scope follow-up tracked in the plan.
function formatMonthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

const VEHICLE_DOCS: Array<{
  type: VehicleDocType;
  slug: 'insurance' | 'puc' | 'fitness' | 'permit' | 'road-tax' | 'registration';
  titleKey: string;
  expiryField:
    | 'insuranceExpiry'
    | 'pucExpiry'
    | 'fitnessExpiry'
    | 'permitExpiry'
    | 'roadTaxExpiry'
    | 'registrationExpiry';
}> = [
  { type: 'INSURANCE',    slug: 'insurance',    titleKey: 'vehicleDoc.insuranceTitle',    expiryField: 'insuranceExpiry' },
  { type: 'PUC',          slug: 'puc',          titleKey: 'vehicleDoc.pucTitle',          expiryField: 'pucExpiry' },
  { type: 'FITNESS',      slug: 'fitness',      titleKey: 'vehicleDoc.fitnessTitle',      expiryField: 'fitnessExpiry' },
  { type: 'PERMIT',       slug: 'permit',       titleKey: 'vehicleDoc.permitTitle',       expiryField: 'permitExpiry' },
  { type: 'ROAD_TAX',     slug: 'road-tax',     titleKey: 'vehicleDoc.roadTaxTitle',      expiryField: 'roadTaxExpiry' },
  { type: 'REGISTRATION', slug: 'registration', titleKey: 'vehicleDoc.registrationTitle', expiryField: 'registrationExpiry' },
];

type PaperStatus = 'valid' | 'warn' | 'danger';

function paperStatus(date: string): { status: PaperStatus; days: number } {
  const d = daysUntil(date) ?? 0;
  if (d < 0) return { status: 'danger', days: d };
  if (d <= 30) return { status: 'warn', days: d };
  return { status: 'valid', days: d };
}

function PaperIcon({ kind }: { kind: 'insurance' | 'puc' | 'fitness' | 'permit' }) {
  const common = {
    width: 22, height: 22, viewBox: '0 0 24 24',
    fill: 'none', stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  if (kind === 'insurance') {
    return (
      <svg {...common} aria-hidden>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    );
  }
  if (kind === 'puc') {
    return (
      <svg {...common} aria-hidden>
        <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.2 2.7c.7 6.1.3 11.5-2.2 14.1A6 6 0 0 1 11 20z" />
        <path d="M2 21c0-3 2-7 6-8" />
      </svg>
    );
  }
  if (kind === 'fitness') {
    return (
      <svg {...common} aria-hidden>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    );
  }
  // permit
  return (
    <svg {...common} aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function PaperTile({
  kind, label, date,
}: { kind: 'insurance' | 'puc' | 'fitness' | 'permit'; label: string; date: string }) {
  const { t } = useTranslation('vehicle');
  const { status, days } = paperStatus(date);

  const borderLeft =
    status === 'danger' ? '3px solid var(--color-danger,#C0362B)' :
    status === 'warn' ? '3px solid #C58E3A' : undefined;

  const iconBg =
    status === 'danger' ? 'var(--status-danger-bg,#FCE7E4)' :
    status === 'warn' ? 'var(--status-warning-bg,#FBF1DE)' :
    'var(--mint-soft)';
  const iconFg =
    status === 'danger' ? 'var(--color-danger,#C0362B)' :
    status === 'warn' ? 'var(--status-warning-fg,#8A5E1D)' :
    'var(--green-700)';

  const pillCls =
    status === 'danger'
      ? 'bg-[var(--status-danger-bg,#FCE7E4)] text-[var(--color-danger,#C0362B)]'
      : status === 'warn'
      ? 'bg-[var(--status-warning-bg,#FBF1DE)] text-[var(--status-warning-fg,#8A5E1D)]'
      : 'bg-[var(--mint-soft)] text-[var(--green-700)]';

  const pillLabel =
    status === 'danger'
      ? t('expired', { days: Math.abs(days) })
      : status === 'warn'
      ? days === 0 ? t('expiringToday', { defaultValue: 'Today' }) : t('expiringSoon', { days })
      : t('valid', { defaultValue: 'Valid' });

  return (
    <div
      className="flex gap-[14px] items-start mt-[10px] p-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[14px] shadow-[var(--shadow-card)]"
      style={{ borderLeft }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0 rounded-[12px]"
        style={{ width: 44, height: 44, background: iconBg, color: iconFg }}
      >
        <PaperIcon kind={kind} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-[16px] font-extrabold tracking-tight text-[var(--color-text)]">{label}</div>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${pillCls}`}
          >
            <span
              aria-hidden
              className="rounded-full"
              style={{ width: 6, height: 6, background: 'currentColor' }}
            />
            {pillLabel}
          </span>
        </div>
        <div
          className={`text-xs ${status === 'danger' ? 'font-semibold text-[var(--color-danger,#C0362B)]' : 'text-[var(--color-text-secondary)]'}`}
        >
          {t('expiresOn', { date: formatISTDate(date) })}
        </div>
      </div>
    </div>
  );
}

export function VehicleRoute() {
  const { t } = useTranslation('vehicle');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState<VehicleResp['vehicle']>(null);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);

  const load = async () => {
    setError(false); setLoading(true);
    try {
      const result = await cachedGet<VehicleResp>('/driver-app/me/vehicle');
      setVehicle(result.data.vehicle);
      setFetchedAt(Date.now());
      setStale(result.state === 'stale');
    } catch { setError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  if (loading && !vehicle) return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  if (error && !vehicle) return <ErrorState onRetry={load} />;

  if (!vehicle) {
    return <EmptyState title={t('noVehicleAssigned')} subtitle={t('noVehicleSubtitle')} />;
  }

  // Only render paper tiles where expiry is non-null (per data audit: ~40% populated)
  const papers = ([
    { kind: 'insurance' as const, label: t('insurance'), date: vehicle.insuranceExpiry ?? '' },
    { kind: 'puc'       as const, label: t('puc'),       date: vehicle.pucExpiry ?? '' },
    { kind: 'fitness'   as const, label: t('fitness'),   date: vehicle.fitnessExpiry ?? '' },
    { kind: 'permit'    as const, label: t('permit'),    date: vehicle.permitExpiry ?? '' },
  ]).filter(p => p.date);

  return (
    <div>
      {stale && fetchedAt ? <StaleBanner fetchedAt={fetchedAt} onRefresh={load} /> : null}
      <div className="px-5 pt-2 pb-10">
        <h1 className="text-2xl font-extrabold text-[var(--color-text)] mb-4">{t('myVehicle')}</h1>

        {/* Vehicle hero — mint icon tile, plate, Fraunces model name */}
        <div
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-card)] shadow-[var(--shadow-card)] overflow-hidden"
        >
          <div className="flex items-center gap-[14px] p-5 pb-[18px]">
            <div
              className="flex items-center justify-center flex-shrink-0 rounded-[18px]"
              style={{ width: 60, height: 60, background: 'var(--mint-soft)', color: 'var(--green-700)' }}
            >
              <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="2" y="11" width="20" height="7" rx="1" />
                <path d="M5 11V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
                <circle cx="7" cy="18" r="1.5" />
                <circle cx="17" cy="18" r="1.5" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[22px] font-extrabold text-[var(--color-text)]"
                style={{ letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums' }}
              >
                {vehicle.regNumber}
              </div>
              <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                {vehicle.make} <span className="serif-accent text-[15px]">{vehicle.model}</span>
              </div>
            </div>
          </div>

          {/* KYC chip strip — only renders the fields the vehicle response actually carries.
              Today that's hasImage (photo); RC/insurance/PUC status are surfaced via
              the paper tiles below, not as separate KYC chips. */}
          {vehicle.hasImage ? (
            <div
              className="flex flex-wrap gap-2 px-5 py-3 border-t border-[var(--color-border)]"
              style={{ background: 'var(--color-surface-soft)' }}
            >
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                style={{
                  background: 'var(--mint-soft)',
                  color: 'var(--green-700)',
                  border: '1px solid var(--mint-line)',
                }}
              >
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
                {t('photoUploaded', { defaultValue: '✓ Photo uploaded' })}
              </span>
            </div>
          ) : null}
        </div>

        {/* Papers */}
        {papers.length > 0 ? (
          <>
            <h2
              className="text-[11px] font-bold uppercase mt-[22px] mb-2.5 text-[var(--color-text-dim,#9AA3AC)]"
              style={{ letterSpacing: '0.08em' }}
            >
              {t('papers')}
            </h2>
            {papers.map(p => (
              <PaperTile key={p.kind} kind={p.kind} label={p.label} date={p.date} />
            ))}
          </>
        ) : null}

        {/* Vehicle documents — UPLOADED tap-throughs to the doc viewer,
            MISSING cards render as non-clickable (no dead-button feel).
            Fixed render order: Insurance / PUC / Fitness / Permit /
            Road Tax / Registration. */}
        <h2
          className="text-[11px] font-bold uppercase tracking-wider mb-2 mt-6"
          style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}
        >
          {t('vehicleDoc.sectionTitle')}
        </h2>
        <div className="space-y-2 mb-4">
          {VEHICLE_DOCS.map(doc => {
            const docMeta = vehicle.complianceDocs?.find(d => d.type === doc.type);
            const uploaded = !!docMeta;
            const expiryIso = vehicle[doc.expiryField];
            const expiryFmt = formatMonthYear(expiryIso);
            const lastUpdatedFmt = formatMonthYear(docMeta?.updatedAt);

            const subtitle = uploaded
              ? (expiryFmt
                  ? lastUpdatedFmt
                    ? t('vehicleDoc.subtitleExpires', { date: expiryFmt }) + ' · ' + t('vehicleDoc.lastUpdated', { date: lastUpdatedFmt })
                    : t('vehicleDoc.subtitleExpires', { date: expiryFmt })
                  : t('vehicleDoc.subtitleOnFile'))
              : (expiryFmt
                  ? t('vehicleDoc.subtitleExpiresAndMissing', { date: expiryFmt })
                  : t('vehicleDoc.subtitleMissingHint'));

            const cardStyle: CSSProperties = uploaded
              ? { background: 'var(--mint-soft)', border: '1px solid var(--mint-line)', borderRadius: 14 }
              : {
                  background: 'var(--status-warning-bg,#FBF1DE)',
                  border: '1px solid rgba(197, 142, 58, 0.35)',
                  borderLeft: '4px solid var(--status-warning-fg,#8A5E1D)',
                  borderRadius: 14,
                };

            const pillStyle: CSSProperties = uploaded
              ? { background: 'var(--mint-soft)', color: 'var(--green-700)', border: '1px solid var(--mint-line)' }
              : {
                  background: 'var(--status-warning-bg,#FBF1DE)',
                  color: 'var(--status-warning-fg,#8A5E1D)',
                  border: '1px solid rgba(197, 142, 58, 0.35)',
                };

            const cardContent = (
              <div className="flex items-start gap-3 p-4" style={{ minHeight: 'var(--touch-target, 52px)' }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: uploaded ? 'rgba(46,158,101,0.10)' : 'rgba(197,142,58,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: uploaded ? 'var(--green-700)' : 'var(--status-warning-fg,#8A5E1D)',
                    flexShrink: 0,
                  }}
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold text-[var(--color-text)]">{t(doc.titleKey)}</span>
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                      style={pillStyle}
                    >
                      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
                      {uploaded ? t('profile:kyc.statusUploaded') : t('profile:kyc.statusMissing')}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)] mb-1">{subtitle}</div>
                  {uploaded ? (
                    <div className="text-xs font-semibold text-[var(--green-700)]">{t('vehicleDoc.viewDocument')} ›</div>
                  ) : null}
                </div>
              </div>
            );

            return uploaded ? (
              <button
                key={doc.type}
                type="button"
                onClick={() => {
                  haptic.tap();
                  navigate(`/vehicle/compliance/${doc.slug}/view`);
                }}
                className="w-full text-left"
                style={cardStyle}
              >
                {cardContent}
              </button>
            ) : (
              <div key={doc.type} style={cardStyle}>
                {cardContent}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-[var(--color-text-secondary)] mt-6 text-center leading-relaxed">
          {t('opsNote', { defaultValue: 'Vehicle papers are managed by your operator. Talk to ops to renew expiring or expired documents.' })}
        </p>
      </div>
    </div>
  );
}
